import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Coffee, Plus, Gift, ChartLine, Megaphone, ArrowRight, Confetti, Sparkle, CheckCircle, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const INK = "#0A0A0A";
const IVORY = "#F5EFE6";
const IVORY_LIGHT = "#FBF7F0";
const ACCENT = "#E94E3B";
const GRAD = "linear-gradient(135deg, #0A0A0A, #1A1A1A)";

const TOP_CUSTOMERS = [
  { name: "Maya R.", stamps: 42, redemptions: 4, status: "VIP" },
  { name: "Ben K.", stamps: 28, redemptions: 2, status: "Loyal" },
  { name: "Lola T.", stamps: 19, redemptions: 1, status: "Loyal" },
  { name: "Chris J.", stamps: 14, redemptions: 1, status: "Active" },
  { name: "Priya S.", stamps: 7, redemptions: 0, status: "New" },
];

const PROMO_OPTIONS = [
  { id: "rainy", icon: "☂", title: "Rainy Tuesday — double stamps", body: "Every coffee earns 2 stamps · ends 6pm", badge: "Today only" },
  { id: "birthday", icon: "🎂", title: "Birthday month — free slice of cake", body: "Your treat, on us this March", badge: "This month" },
  { id: "early", icon: "🌅", title: "Before 9am — free extra shot", body: "Double up your morning cup", badge: "Mornings" },
  { id: "winback", icon: "🔄", title: "Missed you — 3 free stamps", body: "A little nudge to pop back in", badge: "For you" },
];

// Simple scannable QR-style block (not a real QR, just looks convincing)
function MockQR({ size = 110 }) {
  const cells = 10;
  const pattern = [
    [1,1,1,1,1,0,1,1,1,1],
    [1,0,0,0,1,0,1,0,0,1],
    [1,0,1,0,1,1,0,1,0,1],
    [1,0,0,0,1,0,1,0,0,1],
    [1,1,1,1,1,0,1,1,1,1],
    [0,0,0,0,0,0,0,0,0,0],
    [1,0,1,1,0,1,1,1,1,1],
    [0,1,0,1,1,0,0,0,0,1],
    [1,1,0,0,1,1,1,0,1,1],
    [1,0,1,1,0,0,1,0,0,1],
  ];
  const cs = size / cells;
  return (
    <div className="bg-white p-2" style={{ width: size + 16, height: size + 16 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {pattern.map((row, y) => row.map((c, x) => c ? <rect key={`${x}-${y}`} x={x * cs} y={y * cs} width={cs} height={cs} fill={INK} /> : null))}
      </svg>
    </div>
  );
}

export default function StampCard() {
  const [view, setView] = useState("customer"); // customer | owner
  const [stamps, setStamps] = useState(7);
  const [goal] = useState(10);
  const [redeemed, setRedeemed] = useState(false);
  const [activePromo, setActivePromo] = useState(PROMO_OPTIONS[0]); // default: rainy tuesday
  const [pushedAtOwner, setPushedAtOwner] = useState(false);
  const navigate = useNavigate();

  const addStamp = () => {
    if (stamps >= goal) return;
    const next = stamps + 1;
    setStamps(next);
    if (next >= goal) {
      toast.success("🎉 Reward unlocked — free coffee!", { duration: 4000 });
    } else {
      toast.success(`Stamp added! ${goal - next} to your free coffee`);
    }
  };

  const openRedeem = () => setRedeemed(true);
  const markUsed = () => {
    setRedeemed(false);
    setStamps(0);
    toast.success("Used · card reset for the next round");
  };
  const closeRedeem = () => setRedeemed(false);

  const reset = () => {
    setStamps(7);
    setRedeemed(false);
    setPushedAtOwner(false);
    setActivePromo(PROMO_OPTIONS[0]);
  };

  const pushPromo = (p) => {
    setActivePromo(p);
    setPushedAtOwner(true);
    toast.success(`Pushed to 143 members — "${p.title}"`);
  };

  return (
    <div className="min-h-screen" style={{ background: IVORY, color: "#1F2937" }} data-testid="stamp-card-interactive">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-sm border-b" style={{ borderColor: "#E5DFD5" }}>
        <div className="max-w-4xl mx-auto px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Link to="/demos/stamp-card" className="text-zinc-400 hover:text-zinc-900"><ArrowLeft size={18} /></Link>
            <div className="h-9 w-9 rounded-full flex items-center justify-center" style={{ background: INK }}>
              <Coffee size={16} weight="fill" color={ACCENT} />
            </div>
            <div>
              <div className="text-sm font-bold">Coffee Club</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Concept mock</div>
            </div>
          </div>
          <div className="flex gap-1 bg-white border border-zinc-200 p-1 rounded-full" data-testid="stamp-view-toggle">
            {["customer", "owner"].map((v) => (
              <button key={v} onClick={() => setView(v)} className={`px-3 py-1 text-xs font-semibold rounded-full capitalize transition-all ${view === v ? "text-white" : "text-zinc-600"}`} style={{ background: view === v ? GRAD : "transparent" }} data-testid={`stamp-view-${v}`}>
                {v}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-10">
        {view === "customer" ? (
          <CustomerView stamps={stamps} goal={goal} addStamp={addStamp} openRedeem={openRedeem} redeemed={redeemed} markUsed={markUsed} closeRedeem={closeRedeem} reset={reset} activePromo={activePromo} pushedAtOwner={pushedAtOwner} />
        ) : (
          <OwnerView activePromo={activePromo} pushPromo={pushPromo} pushedAtOwner={pushedAtOwner} />
        )}
      </main>

      <footer className="max-w-4xl mx-auto px-5 pb-12 text-center">
        <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">Concept · ~2–3 week build</p>
        <Link to="/consultation?demo=stamp-card" className="inline-flex items-center gap-2 px-5 py-3 text-white text-sm font-semibold rounded-full" style={{ background: GRAD }}>
          Want one for your shop? <ArrowRight size={14} weight="bold" />
        </Link>
      </footer>
    </div>
  );
}

function CustomerView({ stamps, goal, addStamp, openRedeem, redeemed, markUsed, closeRedeem, reset, activePromo, pushedAtOwner }) {
  const unlocked = stamps >= goal;
  return (
    <div className="mx-auto max-w-sm">
      <p className="text-center text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">On your phone</p>
      <div className="relative mx-auto border-[10px] border-zinc-900 rounded-[2.5rem] overflow-hidden" style={{ width: 340, aspectRatio: "9 / 16" }}>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-5 bg-zinc-900 rounded-b-2xl z-10" />
        <div className="w-full h-full flex flex-col" style={{ background: IVORY }}>
          <div className="p-5 text-white" style={{ background: INK }}>
            <div className="flex items-center justify-between">
              <p className="text-[10px] font-mono uppercase tracking-[0.3em] opacity-80">Birch &amp; Bean</p>
              <Coffee size={14} weight="fill" style={{ color: ACCENT }} />
            </div>
            <h1 className="text-3xl font-black tracking-tight mt-1">Coffee Club</h1>
            <p className="text-xs opacity-80 mt-1.5">Get a free coffee on your 10th.</p>
          </div>

          <div className="flex-1 p-5 flex flex-col">
            <div className="grid grid-cols-5 gap-2 mb-3" data-testid="stamp-grid">
              {[...Array(goal)].map((_, i) => {
                const on = i < stamps;
                return (
                  <div key={i} className="aspect-square rounded-full flex items-center justify-center border-2" style={{ background: on ? ACCENT : "white", borderColor: on ? ACCENT : "#E5DFD5" }}>
                    {on && <Coffee size={16} weight="fill" color="white" />}
                  </div>
                );
              })}
            </div>
            <div className="text-center">
              <div className="text-2xl font-black" style={{ color: INK }}>{stamps}<span className="text-zinc-400 text-base font-bold"> / {goal}</span></div>
              <p className="text-[11px] text-zinc-500">{unlocked ? "Free coffee unlocked!" : `${goal - stamps} to your free coffee`}</p>
            </div>

            {/* Active promotion — always visible to the customer when a promo is set */}
            {!unlocked && activePromo && (
              <div className="mt-3 p-3 border relative overflow-hidden" style={{ borderColor: ACCENT + "55", backgroundColor: "white" }} data-testid="stamp-customer-promo">
                <div className="absolute -right-4 -top-4 w-16 h-16 rounded-full opacity-10" style={{ backgroundColor: ACCENT }} />
                <div className="relative">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Sparkle size={11} weight="fill" style={{ color: ACCENT }} />
                    <span className="text-[9px] font-mono uppercase tracking-[0.3em] font-bold" style={{ color: ACCENT }}>{activePromo.badge}</span>
                    {pushedAtOwner && <span className="ml-auto text-[8px] font-mono text-emerald-600 flex items-center gap-0.5"><span className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse" /> JUST IN</span>}
                  </div>
                  <div className="text-sm font-black leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{activePromo.icon} {activePromo.title}</div>
                  <p className="text-[11px] text-zinc-500 mt-0.5 leading-snug">{activePromo.body}</p>
                </div>
              </div>
            )}

            <div className="mt-auto pt-3">
              {unlocked ? (
                <button onClick={openRedeem} className="w-full py-3 text-white font-bold rounded-xl flex items-center justify-center gap-2" style={{ background: ACCENT }} data-testid="stamp-redeem">
                  <Gift size={16} weight="fill" /> Redeem free coffee
                </button>
              ) : (
                <div className="text-center text-[11px] text-zinc-500">Staff taps to add a stamp at the till</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Staff tap area */}
      <div className="mt-6 bg-white border-2 border-dashed border-zinc-300 p-4 rounded-xl" data-testid="stamp-staff-tap">
        <p className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2 text-center">Staff device · tap to stamp</p>
        <div className="flex gap-2 justify-center">
          <button onClick={addStamp} disabled={stamps >= goal || redeemed} className="flex-1 py-3 text-white font-bold rounded-lg flex items-center justify-center gap-2 disabled:opacity-50" style={{ background: GRAD }} data-testid="stamp-add-btn">
            <Plus size={16} weight="bold" /> Add stamp
          </button>
          <button onClick={reset} className="px-4 py-3 text-xs font-semibold text-zinc-600 bg-zinc-100 rounded-lg hover:bg-zinc-200">Reset demo</button>
        </div>
      </div>

      {redeemed && <RedeemSheet markUsed={markUsed} closeRedeem={closeRedeem} />}
    </div>
  );
}

function RedeemSheet({ markUsed, closeRedeem }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={closeRedeem} data-testid="stamp-redeem-sheet">
      <div className="relative bg-white w-full max-w-sm overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Celebratory top band */}
        <div className="relative p-6 pb-5 text-white overflow-hidden" style={{ background: INK }}>
          <div className="absolute -top-6 -right-6 w-32 h-32 rounded-full opacity-20" style={{ backgroundColor: ACCENT }} />
          <div className="absolute -bottom-8 -left-8 w-24 h-24 rounded-full opacity-10" style={{ backgroundColor: ACCENT }} />
          <div className="relative">
            <div className="flex items-center justify-between mb-3">
              <span className="text-[10px] font-mono uppercase tracking-[0.35em]" style={{ color: ACCENT }}>Free coffee unlocked</span>
              <button onClick={closeRedeem} className="text-white/60 hover:text-white"><X size={16} /></button>
            </div>
            <div className="flex items-center gap-2">
              <Confetti size={26} weight="fill" style={{ color: ACCENT }} />
              <h2 className="text-2xl font-black tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Show this at the till</h2>
            </div>
          </div>
        </div>

        <div className="p-6 flex flex-col items-center" style={{ background: IVORY_LIGHT }}>
          <div className="shadow-lg mb-4" style={{ background: "white", padding: 8 }}>
            <MockQR size={150} />
          </div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Or enter this code</div>
          <div className="font-mono text-3xl tracking-[0.25em] font-black mb-3" style={{ color: INK }}>CC-7Q2P</div>
          <div className="w-full pt-3 border-t flex items-center justify-between" style={{ borderColor: "#E5DFD5" }}>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Reward</div>
              <div className="text-sm font-bold">1× Free coffee</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Valid</div>
              <div className="text-sm font-bold">Today only</div>
            </div>
          </div>
        </div>

        <div className="p-4 space-y-2" style={{ background: "white", borderTop: "1px solid #E5DFD5" }}>
          <button onClick={markUsed} className="w-full py-3 text-white font-bold rounded-lg flex items-center justify-center gap-2" style={{ background: ACCENT }} data-testid="stamp-mark-used">
            <CheckCircle size={16} weight="fill" /> Mark as used
          </button>
          <p className="text-[10px] text-center text-zinc-500">Staff taps this once they've poured your free coffee</p>
        </div>
      </div>
    </div>
  );
}

function OwnerView({ activePromo, pushPromo, pushedAtOwner }) {
  return (
    <div>
      <h1 className="text-3xl font-black tracking-tight mb-1">Owner dashboard</h1>
      <p className="text-sm text-zinc-600 mb-8">Who's loyal, how often they come, and how to nudge them back.</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        {[
          { label: "Active members", value: "143", trend: "+12 this week" },
          { label: "Stamps this week", value: "287", trend: "↑ 18%" },
          { label: "Redemptions", value: "11", trend: "All free coffee" },
          { label: "Churn risk", value: "9", trend: "Haven't been in 3wks" },
        ].map((k) => (
          <div key={k.label} className="bg-white border border-zinc-200 p-4 rounded-xl">
            <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{k.label}</p>
            <div className="text-2xl font-black mt-1">{k.value}</div>
            <p className="text-xs text-zinc-500 mt-0.5">{k.trend}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-zinc-100 flex items-center gap-2">
            <ChartLine size={16} weight="fill" style={{ color: ACCENT }} />
            <h2 className="font-bold text-sm">Top customers</h2>
          </div>
          <ul data-testid="stamp-top-customers">
            {TOP_CUSTOMERS.map((c) => (
              <li key={c.name} className="px-5 py-3 flex items-center justify-between border-b border-zinc-100 last:border-b-0">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-full flex items-center justify-center text-white font-bold text-sm" style={{ background: GRAD }}>
                    {c.name[0]}
                  </div>
                  <div>
                    <div className="font-semibold text-sm">{c.name}</div>
                    <div className="text-xs text-zinc-500 font-mono">{c.stamps} stamps · {c.redemptions} redemptions</div>
                  </div>
                </div>
                <span className="text-[10px] font-mono uppercase tracking-widest font-bold px-2 py-1 rounded-full" style={{ color: ACCENT, backgroundColor: "#FDEEEA" }}>{c.status}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="bg-white border border-zinc-200 rounded-xl p-5" data-testid="stamp-promo">
          <div className="flex items-center gap-2 mb-2">
            <Megaphone size={16} weight="fill" style={{ color: ACCENT }} />
            <h2 className="font-bold text-sm">Push a promo</h2>
          </div>
          <p className="text-sm text-zinc-600 mb-4">Pushes instantly to all 143 active members. Only one runs at a time.</p>
          <div className="space-y-2 mb-4">
            {PROMO_OPTIONS.map((p) => {
              const isActive = activePromo?.id === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => pushPromo(p)}
                  className="w-full text-left p-3 text-sm rounded-lg border-2 transition-all flex items-start gap-2"
                  style={{
                    borderColor: isActive ? ACCENT : "#E5DFD5",
                    backgroundColor: isActive ? "#FDEEEA" : "white",
                  }}
                  data-testid={`stamp-promo-${p.id}`}
                >
                  <span className="text-lg flex-shrink-0">{p.icon}</span>
                  <div className="flex-1">
                    <div className="font-semibold leading-tight">{p.title}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">{p.body}</div>
                  </div>
                  {isActive && <CheckCircle size={16} weight="fill" style={{ color: ACCENT }} className="flex-shrink-0 mt-0.5" />}
                </button>
              );
            })}
          </div>
          {pushedAtOwner && (
            <div className="text-xs p-3 rounded-lg text-white text-center font-semibold flex items-center justify-center gap-1.5" style={{ background: INK }}>
              <Sparkle size={12} weight="fill" style={{ color: ACCENT }} /> Live now · switch to Customer view to see it
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
